// src/pages/AdminDashboard.tsx - GERÇEK API ENTEGRASYONU
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Package, 
  CreditCard, 
  TrendingUp, 
  Search,
  Eye,
  Ban,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Mail,
  Phone,
  Calendar,
  Settings,
  LogOut,
  Shield,
  Activity,
  Building2,
  Tag,
  CheckSquare,
  DollarSign,
  Square,
  Bell,
  AlertTriangle,
  Headphones,
  Calculator,
  Bug,
  KeyRound
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import { apiService } from '../services/api';
import type {
  AdminDashboardStats,
  AdminUsersListResponse,
  AdminUserDetails,
  AdminOrdersListResponse,
  AdminOrder,
  AsinWorkerHealth
} from '../services/api';
import AdminOrganizations from './AdminOrganizations';
import AffiliateTagsSection from './AffiliateTagsSection';
import AdminEarningsSection from './AdminEarningsSection';
import AdminAffiliateSection from './AdminAffiliateSection';
import AdminTagReport from './AdminTagReport';
import AdminNotifications from './AdminNotifications';
import AdminSourceReport from './AdminSourceReport';
import AdminExtensionConfig from './AdminExtensionConfig';
import AdminUrlTrace from './AdminUrlTrace';
import AdminSyncRuns from './AdminSyncRuns';
import StaffDashboard from './StaffDashboard';
import AdminAccounting from './AdminAccounting';
import AdminExtensionLogs from './AdminExtensionLogs';
import AdminApiKeys from './AdminApiKeys';
import { useNavigate, useLocation } from 'react-router-dom';

interface AdminDashboardProps {
  onLogout?: () => void;
}

// ✅ YENİ: Göreli zaman formatı (örn. "2 saat önce", "Dün")
function formatRelativeTime(isoString: string | null | undefined): string {
  if (!isoString) return 'Hiç güncellenmedi';

  // Backend UTC olarak gönderiyor; "Z" yoksa ekle ki JS doğru parse etsin
  const utcStr = isoString.endsWith('Z') || isoString.includes('+')
    ? isoString
    : isoString + 'Z';

  const updateDate = new Date(utcStr);
  const now = new Date();
  const diffMs = now.getTime() - updateDate.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return 'Az önce';
  if (diffMinutes < 60) return `${diffMinutes} dakika önce`;
  if (diffHours < 24) return `${diffHours} saat önce`;
  if (diffDays === 1) return 'Dün';
  if (diffDays < 30) return `${diffDays} gün önce`;
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths} ay önce`;
}

export default function AdminDashboard({ onLogout }: AdminDashboardProps) {
  // Aktif görünüm artık URL'den okunur (/admin, /admin/users, /admin/users/:id, /admin/tag-report ...)
  const navigate = useNavigate();
  const location = useLocation();
  const adminSeg = location.pathname.replace(/^\/admin\/?/, '').split('/').filter(Boolean);
  const section = adminSeg[0] || '';
  const routeUserId = section === 'users' ? (adminSeg[1] ? decodeURIComponent(adminSeg[1]) : null) : null;

  const view: 'overview' | 'users' | 'user-detail' | 'orders' | 'organizations' | 'affiliate-tags' | 'earnings' | 'affiliate' | 'tag-report' | 'notifications' | 'source-report' | 'extension-config' | 'url-trace' | 'staff-panel' | 'accounting' | 'extension-logs' | 'api-keys' | 'sync-runs' =
    section === 'users' ? (routeUserId ? 'user-detail' : 'users')
      : section === 'orders' ? 'orders'
      : section === 'organizations' ? 'organizations'
      : section === 'affiliate-tags' ? 'affiliate-tags'
      : section === 'earnings' ? 'earnings'
      : section === 'affiliate' ? 'affiliate'
      : section === 'tag-report' ? 'tag-report'
      : section === 'notifications' ? 'notifications'
      : section === 'source-report' ? 'source-report'
      : section === 'extension-config' ? 'extension-config'
      : section === 'url-trace' ? 'url-trace'
      : section === 'staff-panel' ? 'staff-panel'
      : section === 'accounting' ? 'accounting'
      : section === 'extension-logs' ? 'extension-logs'
      : section === 'api-keys' ? 'api-keys'
      : section === 'sync-runs' ? 'sync-runs'
      : 'overview';

  const setView = (v: typeof view) => {
    const path =
      v === 'overview' ? '/admin'
        : v === 'users' ? '/admin/users'
          : v === 'user-detail' ? (routeUserId ? `/admin/users/${routeUserId}` : '/admin/users')
            : `/admin/${v}`;
    navigate(path);
  };

  // Menü öğelerini gerçek <a href> yapmak için yol eşlemesi (sağ tık/Ctrl+tık → yeni sekme)
  const adminPath = (v: typeof view) =>
    v === 'overview' ? '/admin' : v === 'users' ? '/admin/users' : `/admin/${v}`;
  const adminNavItems: { view: typeof view; label: string; icon: any; active: string[] }[] = [
    { view: 'overview', label: 'Genel Bakış', icon: Activity, active: ['overview'] },
    { view: 'users', label: 'Kullanıcılar', icon: Users, active: ['users', 'user-detail'] },
    { view: 'orders', label: 'Siparişler', icon: Package, active: ['orders'] },
    { view: 'organizations', label: 'Organizasyonlar', icon: Building2, active: ['organizations'] },
    { view: 'earnings', label: 'Ödeme Yönetimi', icon: DollarSign, active: ['earnings'] },
    { view: 'affiliate-tags', label: 'Affiliate Tags', icon: Tag, active: ['affiliate-tags'] },
    { view: 'affiliate', label: 'Satış Ortaklığı', icon: Users, active: ['affiliate'] },
    { view: 'tag-report', label: 'Tag Raporu', icon: Calendar, active: ['tag-report'] },
    { view: 'notifications', label: 'Bildirimler', icon: Bell, active: ['notifications'] },
    { view: 'source-report', label: 'Kaynak Raporu', icon: TrendingUp, active: ['source-report'] },
    { view: 'url-trace', label: 'URL / Tag İzleme', icon: Shield, active: ['url-trace'] },
    { view: 'extension-config', label: 'Eklenti Sürümü', icon: Settings, active: ['extension-config'] },
    { view: 'staff-panel', label: 'Staff Paneli', icon: Headphones, active: ['staff-panel'] },
    { view: 'accounting', label: 'Muhasebe', icon: Calculator, active: ['accounting'] },
    { view: 'extension-logs', label: 'Eklenti Logları', icon: Bug, active: ['extension-logs'] },
    { view: 'api-keys', label: 'API Anahtarları', icon: KeyRound, active: ['api-keys'] },
    { view: 'sync-runs', label: 'Senkron Turları', icon: RefreshCw, active: ['sync-runs'] },
  ];
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [users, setUsers] = useState<AdminUsersListResponse | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUserDetails | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>(''); // '' = müşteriler, 'Staff', 'all'
  const [currentPage, setCurrentPage] = useState(1);
  const [orders, setOrders] = useState<AdminOrdersListResponse | null>(null);

  // ASIN worker sağlığı — çöküşte her sayfada kırmızı şerit. (19–25 Eylül 2026'da worker
  // 7 gün hiç çalışmadı, kimse fark etmedi; o haftanın siparişleri kategori oranına düştü.)
  const [workerHealth, setWorkerHealth] = useState<AsinWorkerHealth | null>(null);
  useEffect(() => {
    let alive = true;
    const check = () => apiService.getAsinWorkerHealth()
      .then(h => { if (alive) setWorkerHealth(h); })
      .catch(() => { /* sağlık ucu erişilemezse şerit gösterme — panel çalışmaya devam etsin */ });
    check();
    const t = setInterval(check, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  
  // Tag atama state'leri
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [showTagModal, setShowTagModal] = useState(false);
  const [affiliateTags, setAffiliateTags] = useState<any[]>([]);
  const [selectedTagId, setSelectedTagId] = useState('');
  const [assigning, setAssigning] = useState(false);

  const [orderFilters, setOrderFilters] = useState({
    userEmail: '',
    status: '',
    verification: '',
    searchTerm: '',
    currentPage: 1
  });

  // Admin overview tarih aralığı (varsayılan: son 30 gün)
  const fmtDate = (d: Date) => d.toISOString().slice(0, 10);
  const [dateRange, setDateRange] = useState<{ start: string; end: string; preset: string }>(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 29);
    return { start: fmtDate(start), end: fmtDate(end), preset: '30' };
  });
  const applyPreset = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - (days - 1));
    setDateRange({ start: fmtDate(start), end: fmtDate(end), preset: String(days) });
  };

  // Tag listesini yükle
  const loadAffiliateTags = async () => {
    try {
      const tags = await apiService.getAffiliateTagsDropdown();
      setAffiliateTags(tags);
    } catch (error) {
      console.error('Tag listesi yüklenemedi:', error);
    }
  };

  // Checkbox fonksiyonları
  const toggleSelectUser = (userId: string) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const toggleSelectAllUsers = () => {
    if (users && selectedUsers.size === users.users.length) {
      setSelectedUsers(new Set());
    } else if (users) {
      setSelectedUsers(new Set(users.users.map(u => u.id)));
    }
  };

  const handleBulkAssignTag = async () => {
    if (selectedUsers.size === 0 || !selectedTagId) return;
    
    try {
      setAssigning(true);
      const response = await apiService.bulkAssignTag({
        userIds: Array.from(selectedUsers),
        tagId: selectedTagId
      });
      toast.success(response.message);
      setShowTagModal(false);
      setSelectedUsers(new Set());
      setSelectedTagId('');
    } catch (error: any) {
      toast.error(error.message || 'Tag atanamadı');
    } finally {
      setAssigning(false);
    }
  };

  useEffect(() => {
    loadAffiliateTags();
  }, []);

  useEffect(() => {
    loadDashboardStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.start, dateRange.end]);

  // #3 — yabancı tag'i olan kullanıcılar (Users listesinde kırmızı ⚠ rozeti)
  const [foreignUsers, setForeignUsers] = useState<Set<string>>(new Set());
  useEffect(() => {
    apiService.getAdminUrlTraceSummary()
      .then(s => setForeignUsers(new Set((s.byUser || []).map(u => u.userId))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (view === 'users') {
      loadUsers();
      setSelectedUsers(new Set()); // Sayfa değişince seçimi temizle
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, currentPage, roleFilter]);

  // Arama için debounce
  useEffect(() => {
    if (view === 'users') {
      const timeoutId = setTimeout(() => {
        setCurrentPage(1);
        loadUsers();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (view === 'orders') {
      loadOrders();
    }
  }, [view, orderFilters.currentPage, orderFilters.status, orderFilters.userEmail, orderFilters.verification]);

  const loadDashboardStats = async () => {
    setLoading(true);
    try {
      const data = await apiService.getAdminDashboardStats(dateRange.start, dateRange.end);
      setStats(data);
    } catch (error: any) {
      console.error('Stats error:', error);
      toast.error(error.message || 'Dashboard verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await apiService.getAdminUsers(currentPage, 20, searchTerm, roleFilter || undefined);
      setUsers(data);
    } catch (error: any) {
      console.error('Users error:', error);
      toast.error(error.message || 'Kullanıcılar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const loadUserDetails = async (userId: string) => {
    setLoading(true);
    try {
      const data = await apiService.getAdminUserDetails(userId);
      setSelectedUser(data);
      navigate(`/admin/users/${userId}`); // view URL'den 'user-detail' olur
    } catch (error: any) {
      console.error('User details error:', error);
      toast.error(error.message || 'Kullanıcı detayları yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // Deep-link / sayfa yenileme: /admin/users/:id açıldığında kullanıcı detayını yükle
  useEffect(() => {
    if (view === 'user-detail' && routeUserId && selectedUser?.user?.id !== routeUserId) {
      loadUserDetails(routeUserId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, routeUserId]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const data = await apiService.getAdminOrders(
        orderFilters.currentPage,
        20,
        orderFilters.userEmail.trim() || undefined,
        orderFilters.status || undefined,
        orderFilters.verification || undefined
      );
      setOrders(data);
    } catch (error: any) {
      console.error('Orders error:', error);
      toast.error(error.message || 'Siparişler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserActive = async (userId: string) => {
    try {
      const result = await apiService.toggleAdminUserActive(userId);
      toast.success(result.message);
      loadUsers();
    } catch (error: any) {
      console.error('Toggle active error:', error);
      toast.error(error.message || 'İşlem başarısız oldu');
    }
  };

  // Kullanıcıyı staff (reklamcı/müşteri iletişim) yap / staff'tan çıkar
  const handleToggleStaff = async (userId: string, currentRole: string) => {
    const makeStaff = currentRole !== 'Staff';
    try {
      const result = await apiService.setUserStaffRole(userId, makeStaff);
      toast.success(result.message || (makeStaff ? 'Staff yapıldı' : 'Staff yetkisi kaldırıldı'));
      loadUsers();
    } catch (error: any) {
      toast.error(error.message || 'İşlem başarısız oldu');
    }
  };

  // Otomatik senkron — kullanıcı bazlı tetikle (sunucuya "istek bırak"; kullanıcı
  // tarayıcısını açtığında/haftalık alarmda sessizce çalışır). Anlık değil.
  const [syncingUserId, setSyncingUserId] = useState<string | null>(null);
  const handleRequestSync = async (userId: string) => {
    setSyncingUserId(userId);
    try {
      await apiService.requestUserSync(userId);
      toast.success('Senkron isteği bırakıldı. Kullanıcı tarayıcısını açınca sessizce çalışacak.');
      loadUsers();
    } catch (error: any) {
      toast.error(error?.message || 'İstek bırakılamadı');
    } finally {
      setSyncingUserId(null);
    }
  };

  // Admin: Staff panelini önizle (backend zaten Admin'e izin veriyor)
  if (view === 'staff-panel') {
    return <StaffDashboard onBack={() => navigate('/admin')} />;
  }

  if (loading && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600 font-medium">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Toaster position="top-center" />
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 w-64 bg-white border-r border-gray-200 shadow-lg z-50">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="px-6 py-4 border-b border-gray-200 shrink-0">
            <div className="flex items-center gap-2">
              <Shield className="w-7 h-7 text-red-600" />
              <div>
                <h1 className="text-lg font-bold text-gray-800 leading-tight">Admin Panel</h1>
                <p className="text-xs text-gray-500 leading-tight">EarnBackHub</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-0.5">
            {adminNavItems.map((item) => {
              const Icon = item.icon
              const isActive = item.active.includes(view)
              return (
                <a
                  key={item.view}
                  href={adminPath(item.view)}
                  onClick={(e) => {
                    // Ctrl/Cmd/Shift/Alt veya orta tık → tarayıcı yeni sekmede açsın
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return
                    e.preventDefault()
                    setView(item.view)
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-left no-underline transition-all ${
                    isActive
                      ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-500 font-medium'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-[18px] h-[18px] shrink-0" />
                  <span>{item.label}</span>
                </a>
              )
            })}
          </nav>

          {/* Logout */}
          <div className="p-3 border-t border-gray-200 shrink-0">
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-all"
            >
              <LogOut className="w-[18px] h-[18px] shrink-0" />
              <span>Çıkış Yap</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-64">
        <div className="p-8">
          {/* ASIN worker çöktü uyarısı */}
          {workerHealth?.down && (
            <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-300 bg-red-50 px-5 py-4">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">
                <p className="font-semibold">
                  ASIN worker çalışmıyor
                  {workerHealth.minutesSinceSuccess != null && (
                    <> — son başarı {workerHealth.minutesSinceSuccess >= 120
                      ? `${Math.round(workerHealth.minutesSinceSuccess / 60)} saat`
                      : `${workerHealth.minutesSinceSuccess} dakika`} önce</>
                  )}
                </p>
                <p className="mt-0.5">
                  {workerHealth.pending.toLocaleString('tr-TR')} ASIN komisyon oranı bekliyor. Bu süre boyunca yeni
                  siparişler canlı oran yerine kategori oranıyla hesaplanıyor. Worker sunucusunu ve Amazon Associates
                  oturumunu (token) kontrol edin. ASIN'ler bu arada yakılmıyor; worker düzelince kendiliğinden işlenir.
                </p>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">
              {view === 'overview' && 'Genel Bakış'}
              {view === 'users' && 'Kullanıcı Yönetimi'}
              {view === 'user-detail' && 'Kullanıcı Detayları'}
              {view === 'orders' && 'Sipariş Yönetimi'}
              {view === 'organizations' && 'Organizasyon Yönetimi'}
              {view === 'affiliate-tags' && 'Affiliate Tag Yönetimi'}
              {view === 'affiliate' && 'Satış Ortaklığı Yönetimi'}
              {view === 'earnings' && 'Ödeme Yönetimi'}
              {view === 'tag-report' && 'Tag Raporu'}
              {view === 'notifications' && 'Bildirimler'}
              {view === 'source-report' && 'Kaynak Raporu'}
              {view === 'url-trace' && 'URL / Tag İzleme'}
              {view === 'extension-config' && 'Eklenti Sürümü'}
              {view === 'accounting' && 'Muhasebe'}
              {view === 'extension-logs' && 'Eklenti Logları'}
              {view === 'api-keys' && 'API Anahtarları'}
              {view === 'sync-runs' && 'Senkron Turları'}
            </h2>
            <p className="text-gray-600">
              {view === 'overview' && 'Sistem istatistiklerini görüntüleyin'}
              {view === 'users' && 'Tüm kullanıcıları yönetin'}
              {view === 'user-detail' && 'Kullanıcı detaylı bilgileri'}
              {view === 'orders' && 'Tüm siparişleri görüntüleyin'}
              {view === 'organizations' && 'Şirket ve kuruluşları yönetin'}
              {view === 'affiliate-tags' && 'Kullanıcılara atanacak affiliate tagları yönetin'}
              {view === 'affiliate' && 'Komisyon ödemelerini ve referans kodlarını yönetin'}
              {view === 'earnings' && 'Kullanıcı ödemelerini yönetin ve onaylayın'}
              {view === 'tag-report' && 'Tarih aralığında tag başına sipariş sayısı (associate doğrulama)'}
              {view === 'notifications' && 'Kullanıcılara sistem mesajı / duyuru gönderin'}
              {view === 'source-report' && 'Kayıtların hangi reklam kaynağından (?src=) geldiğini görün'}
              {view === 'url-trace' && 'Sipariş akışında görülen tag\'ler; yabancı tag (hijack) tespiti'}
              {view === 'extension-config' && 'Min sürümün altındaki eklentileri devre dışı bırakın (zorunlu güncelleme)'}
              {view === 'accounting' && 'Dönem bazlı kâr/zarar — kullanıcı payları, gelir/gider ve net kâr'}
              {view === 'extension-logs' && 'Eklentinin her adımı — bir siparişin akışını baştan sona izleyin, hataları nokta atışı görün'}
              {view === 'api-keys' && 'Sunucu-sunucu entegrasyon anahtarları — oluşturun, iptal edin, kötüye kullanımı izleyin'}
              {view === 'sync-runs' && 'Manuel ve otomatik sipariş güncellemeleri ne yaptı — kaç sipariş doğrulandı, nerede takıldı'}
            </p>
          </div>

          {/* Content */}
          {view === 'overview' && stats && (
            <div className="space-y-6">
              {/* Tarih filtresi */}
              <div className="bg-white rounded-xl p-4 shadow-sm flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 text-gray-600 text-sm font-medium mr-1">
                  <Calendar className="w-4 h-4" /> Tarih Aralığı
                </div>
                {[
                  { d: 7, label: 'Son 7 gün' },
                  { d: 30, label: 'Son 30 gün' },
                  { d: 90, label: 'Son 90 gün' },
                ].map(p => (
                  <button
                    key={p.d}
                    onClick={() => applyPreset(p.d)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                      dateRange.preset === String(p.d)
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
                <div className="flex items-center gap-2 ml-auto">
                  <input
                    type="date"
                    value={dateRange.start}
                    max={dateRange.end}
                    onChange={e => setDateRange(r => ({ ...r, start: e.target.value, preset: 'custom' }))}
                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
                  />
                  <span className="text-gray-400 text-sm">→</span>
                  <input
                    type="date"
                    value={dateRange.end}
                    min={dateRange.start}
                    max={fmtDate(new Date())}
                    onChange={e => setDateRange(r => ({ ...r, end: e.target.value, preset: 'custom' }))}
                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
                  />
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-blue-500"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <span className="text-green-600 text-sm font-semibold">
                      +{stats.users.newLast30Days} aralıkta
                    </span>
                  </div>
                  <h3 className="text-gray-600 text-sm mb-1">Toplam Kullanıcı</h3>
                  <p className="text-3xl font-bold text-gray-800">{stats.users.total}</p>
                  <div className="mt-3 flex gap-4 text-sm">
                    <span className="text-green-600">✓ {stats.users.active} Aktif</span>
                    <span className="text-red-600">✗ {stats.users.inactive} Pasif</span>
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-green-500"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <Package className="w-6 h-6 text-green-600" />
                    </div>
                    <span className="text-green-600 text-sm font-semibold">
                      {stats.orders.total > 0 ? Math.round((stats.orders.delivered / stats.orders.total) * 100) : 0}% teslim
                    </span>
                  </div>
                  <h3 className="text-gray-600 text-sm mb-1">Sipariş (seçili aralık)</h3>
                  <p className="text-3xl font-bold text-gray-800">{stats.orders.total}</p>
                  <div className="mt-3 flex gap-4 text-sm">
                    <span className="text-green-600">✓ {stats.orders.delivered} Teslim</span>
                    <span className="text-yellow-600">⏳ {stats.orders.pending} Bekliyor</span>
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-purple-500"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">Sipariş Tutarı</div>
                      <div className="text-sm font-medium text-gray-700">
                        ${stats.earnings.totalOrderAmount.toFixed(2)}
                      </div>
                    </div>
                  </div>
                  <h3 className="text-gray-600 text-sm mb-1">Kazanç (seçili aralık)</h3>
                  <p className="text-3xl font-bold text-gray-800">
                    ${stats.earnings.totalEarnings.toFixed(2)}
                  </p>
                  <div className="mt-3 text-sm">
                    <span className="text-green-600 font-medium">Ort. sipariş: </span>
                    <span className="text-gray-700">${stats.earnings.averageOrderValue.toFixed(2)}</span>
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-orange-500"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-orange-600" />
                    </div>
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="text-gray-600 text-sm mb-1">Ödeme Yöntemleri</h3>
                  <p className="text-3xl font-bold text-gray-800">{stats.paymentMethods.total}</p>
                  <div className="mt-3 text-sm text-gray-600">
                    Toplam kayıtlı hesap
                  </div>
                </motion.div>
              </div>

              {/* Zaman-serisi grafik (seçili aralığa göre) */}
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-800">Sipariş & Kazanç Grafiği</h3>
                  <span className="text-xs text-gray-400">
                    {dateRange.start} → {dateRange.end}
                  </span>
                </div>
                {stats.chartData && stats.chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={320}>
                    <AreaChart data={stats.chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="adminOrders" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.35} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="adminEarnings" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22c55e" stopOpacity={0.35} />
                          <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} tickMargin={8} minTickGap={16} />
                      <YAxis yAxisId="left" stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                      <YAxis yAxisId="right" orientation="right" stroke="#94a3b8" fontSize={12} />
                      <Tooltip
                        formatter={(value: any, name: any) =>
                          name === 'Kazanç ($)' ? [`$${Number(value).toFixed(2)}`, name] : [value, name]
                        }
                      />
                      <Legend />
                      <Area yAxisId="left" type="monotone" dataKey="orders" name="Sipariş" stroke="#3b82f6" fill="url(#adminOrders)" strokeWidth={2} />
                      <Area yAxisId="left" type="monotone" dataKey="delivered" name="Teslim" stroke="#a855f7" fill="transparent" strokeWidth={2} />
                      <Area yAxisId="right" type="monotone" dataKey="earnings" name="Kazanç ($)" stroke="#22c55e" fill="url(#adminEarnings)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[320px] flex items-center justify-center text-gray-400 text-sm">
                    Seçili aralıkta veri yok.
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <button
                  onClick={() => setView('users')}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all text-left group"
                >
                  <div className="flex items-center justify-between mb-3">
                    <Users className="w-8 h-8 text-blue-600" />
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Kullanıcıları Yönet</h3>
                  <p className="text-gray-600 text-sm">Kullanıcı listesini görüntüle, düzenle ve yönet</p>
                </button>

                <button
                  onClick={() => setView('orders')}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all text-left group"
                >
                  <div className="flex items-center justify-between mb-3">
                    <Package className="w-8 h-8 text-green-600" />
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-green-600 transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Siparişleri İncele</h3>
                  <p className="text-gray-600 text-sm">Tüm siparişleri görüntüle ve filtrele</p>
                </button>
              </div>
            </div>
          )}

          {view === 'users' && users && (
            <div className="space-y-6">
              {/* Search */}
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex gap-4 items-center">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Kullanıcı ara (email, isim)..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <select
                    value={roleFilter}
                    onChange={(e) => { setCurrentPage(1); setRoleFilter(e.target.value); }}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    title="Role göre filtrele"
                  >
                    <option value="">Müşteriler</option>
                    <option value="Staff">Staff</option>
                    <option value="all">Tümü</option>
                  </select>
                  <button
                    onClick={loadUsers}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Yenile
                  </button>
                </div>
              </div>

              {/* Seçim Toolbar - YENİ */}
              {selectedUsers.size > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-purple-50 rounded-xl p-4 shadow-lg border border-purple-200 flex items-center justify-between"
                >
                  <span className="text-purple-700 font-semibold">
                    {selectedUsers.size} kullanıcı seçildi
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowTagModal(true)}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                    >
                      <Tag className="w-4 h-4" />
                      Tag Ata
                    </button>
                    <button
                      onClick={() => setSelectedUsers(new Set())}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Seçimi Temizle
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Users Table */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                {loading ? (
                  <div className="p-12 text-center">
                    <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Yükleniyor...</p>
                  </div>
                ) : users.users.length === 0 ? (
                  <div className="p-12 text-center">
                    <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Kullanıcı bulunamadı</p>
                  </div>
                ) : (
                  <>
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          {/* Checkbox Header - YENİ */}
                          <th className="px-4 py-4 text-left w-12">
                            <button 
                              onClick={toggleSelectAllUsers} 
                              className="p-1 hover:bg-gray-200 rounded"
                            >
                              {selectedUsers.size === users.users.length ? (
                                <CheckSquare className="w-5 h-5 text-purple-600" />
                              ) : (
                                <Square className="w-5 h-5 text-gray-400" />
                              )}
                            </button>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Kullanıcı</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">İletişim</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Durum</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Kayıt</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Son Güncelleme</th>
                          <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase">İşlemler</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {users.users.map((user) => (
                          <tr 
                            key={user.id} 
                            className={`hover:bg-gray-50 ${selectedUsers.has(user.id) ? 'bg-purple-50' : ''}`}
                          >
                            {/* Checkbox Cell - YENİ */}
                            <td className="px-4 py-4">
                              <button 
                                onClick={() => toggleSelectUser(user.id)} 
                                className="p-1 hover:bg-gray-200 rounded"
                              >
                                {selectedUsers.has(user.id) ? (
                                  <CheckSquare className="w-5 h-5 text-purple-600" />
                                ) : (
                                  <Square className="w-5 h-5 text-gray-400" />
                                )}
                              </button>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                                  {user.fullName.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <div className="font-semibold text-gray-800 flex items-center gap-1.5">
                                    {user.fullName}
                                    {foreignUsers.has(user.id) && (
                                      <span
                                        title="Yabancı tag tespit edildi — URL/Tag İzleme sayfasına bakın"
                                        className="inline-flex items-center gap-0.5 text-red-600 text-[11px] font-bold bg-red-50 border border-red-200 rounded-full px-1.5 py-0.5"
                                      >
                                        <AlertTriangle className="w-3 h-3" /> Tag
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-sm text-gray-500">{user.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {user.phoneNumber || 'Yok'}
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                                user.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {user.isActive ? '✓ Aktif' : '✗ Pasif'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {new Date(user.createdAtUtc).toLocaleDateString('tr-TR')}
                            </td>
                            <td className="px-6 py-4 text-sm">
                              {/* Manuel ve otomatik tur AYRI gösterilir — ikisi de SyncRuns'tan.
                                  (lastOrderUpdateAt her ikisini karıştırıyordu, lastAutoSyncAtUtc ise
                                  zamanlayıcı damgası — gerçek çalışma zamanı değil.) */}
                              <div className="flex flex-col gap-0.5">
                                <div className="flex items-baseline gap-1.5">
                                  <span className="text-[11px] uppercase tracking-wide text-gray-400 w-12">Manuel</span>
                                  {user.lastManualSyncAtUtc ? (
                                    <span className="text-gray-700">{formatRelativeTime(user.lastManualSyncAtUtc)}</span>
                                  ) : (
                                    <span className="text-gray-400 italic">hiç</span>
                                  )}
                                </div>
                                <div className="flex items-baseline gap-1.5">
                                  <span className="text-[11px] uppercase tracking-wide text-gray-400 w-12">Oto</span>
                                  {user.lastAutoRunAtUtc ? (
                                    <span className="text-gray-700">{formatRelativeTime(user.lastAutoRunAtUtc)}</span>
                                  ) : (
                                    <span className="text-gray-400 italic">hiç</span>
                                  )}
                                </div>
                              </div>
                              {user.syncRequestedAtUtc ? (
                                <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                                  <RefreshCw className="w-3 h-3" /> Senkron bekliyor
                                </div>
                              ) : null}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => loadUserDetails(user.id)}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                  title="Detayları Gör"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleRequestSync(user.id)}
                                  disabled={syncingUserId === user.id || !!user.syncRequestedAtUtc}
                                  className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                                  title={user.syncRequestedAtUtc ? 'Senkron isteği zaten beklemede' : 'Otomatik senkron tetikle (kullanıcı tarayıcısında sessizce çalışır)'}
                                >
                                  <RefreshCw className={`w-4 h-4 ${syncingUserId === user.id ? 'animate-spin' : ''}`} />
                                </button>
                                <button
                                  onClick={() => handleToggleUserActive(user.id)}
                                  className={`p-2 rounded-lg ${
                                    user.isActive ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'
                                  }`}
                                  title={user.isActive ? 'Pasif Yap' : 'Aktif Yap'}
                                >
                                  {user.isActive ? <Ban className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                                </button>
                                {user.role !== 'Admin' && (
                                  <button
                                    onClick={() => handleToggleStaff(user.id, user.role)}
                                    className={`p-2 rounded-lg ${
                                      user.role === 'Staff' ? 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100' : 'text-gray-400 hover:bg-gray-100'
                                    }`}
                                    title={user.role === 'Staff' ? 'Staff yetkisini kaldır' : 'Staff (reklamcı) yap'}
                                  >
                                    <Headphones className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Pagination */}
                    <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        Toplam {users.total} kullanıcı - Sayfa {users.page} / {Math.ceil(users.total / users.pageSize)}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={users.page === 1}
                          className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setCurrentPage(p => p + 1)}
                          disabled={users.page >= Math.ceil(users.total / users.pageSize)}
                          className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {view === 'user-detail' && selectedUser && (
            <div className="space-y-6">
              <button
                onClick={() => {
                  setView('users');
                  setSelectedUser(null);
                }}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold"
              >
                <ChevronLeft className="w-4 h-4" />
                Geri
              </button>

              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                      {selectedUser.user.fullName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800">{selectedUser.user.fullName}</h2>
                      <p className="text-gray-600">{selectedUser.user.email}</p>
                      {selectedUser.user.signupSource && (
                        <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                          Kaynak: {selectedUser.user.signupSource}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                    selectedUser.user.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {selectedUser.user.isActive ? '✓ Aktif' : '✗ Pasif'}
                  </span>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-gray-600">
                      <Mail className="w-5 h-5" />
                      <span>{selectedUser.user.email}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-600">
                      <Phone className="w-5 h-5" />
                      <span>{selectedUser.user.phoneNumber || 'Yok'}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-600">
                      <Calendar className="w-5 h-5" />
                      <span>{new Date(selectedUser.user.createdAtUtc).toLocaleDateString('tr-TR')}</span>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3">İstatistikler</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Toplam Sipariş:</span>
                        <span className="font-semibold">{selectedUser.stats.totalOrders}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Teslim:</span>
                        <span className="font-semibold text-green-600">{selectedUser.stats.deliveredOrders}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Bekleyen:</span>
                        <span className="font-semibold text-yellow-600">{selectedUser.stats.pendingOrders}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-gray-200">
                        <span className="text-gray-600">Toplam Kazanç:</span>
                        <span className="font-bold text-green-600 text-lg">
                          ${selectedUser.stats.totalEarnings || '0.00'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Methods */}
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Ödeme Yöntemleri ({selectedUser.paymentMethods.length})
                </h3>
                {selectedUser.paymentMethods.length > 0 ? (
                  <div className="space-y-4">
                    {selectedUser.paymentMethods.map((method) => (
                      <div key={method.id} className="p-5 border-2 border-gray-200 rounded-lg bg-gray-50 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-gray-900 text-lg">{method.accountHolderName}</span>
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              method.currency === 'USD' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                            }`}>
                              {method.currency === 'USD' ? '🇺🇸 USD' : '🇹🇷 TRY'}
                            </span>
                          </div>
                          {method.isPrimary && (
                            <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-bold">
                              ⭐ Varsayılan
                            </span>
                          )}
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-4 text-sm">
                          <div className="space-y-2">
                            <div className="flex items-start gap-2">
                              <span className="font-semibold text-gray-700 min-w-[120px]">Ödeme Türü:</span>
                              <span className="text-gray-900 font-medium">{method.payoutMethod}</span>
                            </div>
                            <div className="flex items-start gap-2">
                              <span className="font-semibold text-gray-700 min-w-[120px]">Sağlayıcı:</span>
                              <span className="text-gray-900">{method.paymentProvider}</span>
                            </div>
                            <div className="flex items-start gap-2">
                              <span className="font-semibold text-gray-700 min-w-[120px]">Hesap Sahibi:</span>
                              <span className="text-gray-900">{method.accountOwnerType}</span>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            {method.payoutMethod === 'ACH' && (
                              <>
                                {method.routingNumber && (
                                  <div className="flex items-start gap-2">
                                    <span className="font-semibold text-gray-700 min-w-[120px]">Routing No:</span>
                                    <span className="text-gray-900 font-mono bg-white px-2 py-1 rounded border border-gray-300">
                                      {method.routingNumber}
                                    </span>
                                  </div>
                                )}
                                {method.accountNumber && (
                                  <div className="flex items-start gap-2">
                                    <span className="font-semibold text-gray-700 min-w-[120px]">Hesap No:</span>
                                    <span className="text-gray-900 font-mono bg-white px-2 py-1 rounded border border-gray-300">
                                      {method.accountNumber}
                                    </span>
                                  </div>
                                )}
                                {method.bankAccountType && (
                                  <div className="flex items-start gap-2">
                                    <span className="font-semibold text-gray-700 min-w-[120px]">Hesap Tipi:</span>
                                    <span className="text-gray-900">{method.bankAccountType}</span>
                                  </div>
                                )}
                              </>
                            )}
                            
                            {method.payoutMethod === 'IBAN' && method.iban && (
                              <div className="flex items-start gap-2">
                                <span className="font-semibold text-gray-700 min-w-[120px]">IBAN:</span>
                                <span className="text-gray-900 font-mono bg-white px-2 py-1 rounded border border-gray-300 break-all">
                                  {method.iban}
                                </span>
                              </div>
                            )}

                            {method.accountIdentifier && (
                              <div className="flex items-start gap-2">
                                <span className="font-semibold text-gray-700 min-w-[120px]">Ödeme Bilgisi:</span>
                                <span className="text-gray-900 font-mono bg-white px-2 py-1 rounded border border-gray-300 break-all">
                                  {method.accountIdentifier}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <span className="text-xs text-gray-500">
                            Eklendi: {new Date(method.createdAt).toLocaleDateString('tr-TR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <CreditCard className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>Henüz ödeme yöntemi eklenmemiş</p>
                  </div>
                )}
              </div>

              {/* Plugin Settings */}
              {selectedUser.pluginSettings && (
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Eklenti Ayarları
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Yazılım:</span>
                      <span className="font-semibold">{selectedUser.pluginSettings.software}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cashback %:</span>
                      <span className="font-semibold">{selectedUser.pluginSettings.cashbackPercentage}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Son Güncelleme:</span>
                      <span className="font-semibold">
                        {new Date(selectedUser.pluginSettings.updatedAtUtc).toLocaleDateString('tr-TR')}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {view === 'orders' && orders && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duruma Göre Filtrele
                    </label>
                    <select
                      value={orderFilters.status}
                      onChange={(e) => setOrderFilters({ ...orderFilters, status: e.target.value, currentPage: 1 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Tüm Durumlar</option>
                      <option value="Pending">Beklemede</option>
                      <option value="Delivered">Teslim Edildi</option>
                      <option value="Cancelled">İptal Edildi</option>
                      <option value="Refunded">Para İadesi</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Doğrulama
                    </label>
                    <select
                      value={orderFilters.verification}
                      onChange={(e) => setOrderFilters({ ...orderFilters, verification: e.target.value, currentPage: 1 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      title="Doğrulanmadı: henüz kazanca girmemiş açık siparişler. İptal/iade edilenler bu filtrede görünmez."
                    >
                      <option value="">Tümü</option>
                      <option value="verified">Doğrulandı</option>
                      <option value="unverified">Doğrulanmadı</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Kullanıcı E-postası (opsiyonel)
                    </label>
                    <input
                      type="text"
                      placeholder="Üyelik e-postası ile filtrele..."
                      value={orderFilters.userEmail}
                      onChange={(e) => setOrderFilters({ ...orderFilters, userEmail: e.target.value, currentPage: 1 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="flex items-end">
                    <button
                      onClick={loadOrders}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Yenile
                    </button>
                  </div>
                </div>
              </div>

              {/* Orders Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4 shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Toplam</p>
                      <p className="text-2xl font-bold text-gray-800">{orders.total}</p>
                    </div>
                    <Package className="w-8 h-8 text-blue-600" />
                  </div>
                </div>

                <div className="bg-white rounded-xl p-4 shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Bu Sayfa</p>
                      <p className="text-2xl font-bold text-gray-800">{orders.orders.length}</p>
                    </div>
                    <Package className="w-8 h-8 text-green-600" />
                  </div>
                </div>

                <div className="bg-white rounded-xl p-4 shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Sayfa</p>
                      <p className="text-2xl font-bold text-gray-800">{orders.page}/{Math.ceil(orders.total / orders.pageSize)}</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-orange-600" />
                  </div>
                </div>

                <div className="bg-white rounded-xl p-4 shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Filtrelenmiş</p>
                      <p className="text-2xl font-bold text-gray-800">{orders.orders.length}</p>
                    </div>
                    <Search className="w-8 h-8 text-purple-600" />
                  </div>
                </div>
              </div>

              {/* Orders Table */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                {loading ? (
                  <div className="p-12 text-center">
                    <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Yükleniyor...</p>
                  </div>
                ) : orders.orders.length === 0 ? (
                  <div className="p-12 text-center">
                    <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Sipariş bulunamadı</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Sipariş No</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Kullanıcı</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Alıcı Hesabı</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Durum</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Tutar</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Ürün Sayısı</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Tarih</th>
                            <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase">İşlemler</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {orders.orders.map((order: AdminOrder) => {
                            const getStatusColor = (status: string | null) => {
                              if (!status) return 'bg-gray-100 text-gray-700';
                              const s = status.toLowerCase();
                              if (s.includes('delivered')) return 'bg-green-100 text-green-700';
                              if (s === 'cancelled' || s === 'refunded') return 'bg-red-100 text-red-700';
                              if (s === 'test') return 'bg-yellow-100 text-yellow-700';
                              if (s.includes('arriving')) return 'bg-blue-100 text-blue-700';
                              return 'bg-gray-100 text-gray-700';
                            };

                            return (
                              <tr key={order.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4">
                                  <div className="font-medium text-gray-800">{order.orderId}</div>
                                </td>
                                <td className="px-6 py-4">
                                  <div>
                                    <div className="font-medium text-gray-800">{order.user.fullName}</div>
                                    <div className="text-sm text-gray-500">{order.user.email}</div>
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  {order.buyerEmail
                                    ? <span className="text-sm text-gray-800">{order.buyerEmail}</span>
                                    : <span className="text-sm text-gray-400 italic">Okunmadı</span>}
                                </td>
                                <td className="px-6 py-4">
                                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`}>
                                    {order.status || 'Bilinmiyor'}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="font-semibold text-gray-800">
                                    ${order.total?.toFixed(2) || '0.00'}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-gray-600">{order.itemCount} adet</span>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="text-sm text-gray-600">
                                    {order.orderDateUtc 
                                      ? new Date(order.orderDateUtc).toLocaleDateString('tr-TR')
                                      : new Date(order.createdAtUtc).toLocaleDateString('tr-TR')
                                    }
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex items-center justify-center gap-2">
                                    <button
                                      onClick={() => loadUserDetails(order.user.id)}
                                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                      title="Kullanıcı Detayları"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        Toplam {orders.total} sipariş - Sayfa {orders.page} / {Math.ceil(orders.total / orders.pageSize)}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setOrderFilters({ ...orderFilters, currentPage: Math.max(1, orderFilters.currentPage - 1) })}
                          disabled={orders.page === 1}
                          className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setOrderFilters({ ...orderFilters, currentPage: orderFilters.currentPage + 1 })}
                          disabled={orders.page >= Math.ceil(orders.total / orders.pageSize)}
                          className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {view === 'organizations' && (
            <AdminOrganizations />
          )}

          {view === 'affiliate-tags' && (
            <AffiliateTagsSection />
          )}

{view === 'earnings' && (
  <AdminEarningsSection />
)}

{view === 'affiliate' && (
  <AdminAffiliateSection />
)}

{view === 'tag-report' && (
  <AdminTagReport />
)}

{view === 'notifications' && (
  <AdminNotifications />
)}

{view === 'source-report' && (
  <AdminSourceReport />
)}

{view === 'extension-config' && (
  <AdminExtensionConfig />
)}

{view === 'url-trace' && (
  <AdminUrlTrace />
)}

{view === 'accounting' && (
  <AdminAccounting />
)}

{view === 'extension-logs' && (
  <AdminExtensionLogs />
)}

{view === 'sync-runs' && (
  <AdminSyncRuns />
)}

{view === 'api-keys' && (
  <AdminApiKeys />
)}
        </div>
      </div>

      {/* Tag Atama Modal - YENİ */}
      {showTagModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4"
          >
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Tag className="w-5 h-5 text-purple-600" />
              Toplu Tag Atama
            </h3>

            <p className="text-gray-600 mb-4">
              <strong>{selectedUsers.size}</strong> kullanıcıya tag atanacak.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Affiliate Tag Seçin
              </label>
              <select
                value={selectedTagId}
                onChange={(e) => setSelectedTagId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="">-- Tag Seçin --</option>
                {affiliateTags.map((tag: any) => (
                  <option key={tag.id} value={tag.id}>
                    {tag.tagCode} {tag.isDefault ? '(Varsayılan)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleBulkAssignTag}
                disabled={!selectedTagId || assigning}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {assigning ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Atanıyor...
                  </>
                ) : (
                  <>
                    <Tag className="w-4 h-4" />
                    Tag Ata
                  </>
                )}
              </button>
              <button
                onClick={() => { setShowTagModal(false); setSelectedTagId(''); }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                İptal
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}